import { AddCircleOutlineOutlined } from "@mui/icons-material";
import Delete from "@mui/icons-material/Delete";
import { Box, Grid, IconButton, MenuItem, Typography } from "@mui/material";
import { FieldArray, Formik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Form } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import getAxiosError from "../../../app/api/axiosError";
import {
  getfdpList,
  getfdpToParcelCenterMappingList,
} from "../../../app/features/fdp/fdpSlice";
import { addFdpMapping } from "../../../app/features/parcelCenter/parcelCenterSlice";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import {
  AddParcelCenterFdpMappingRequest,
  MultiFdpPcMapping,
} from "../../../app/models/requestModels/parcelCenterRequest";
import FormikTextField from "../../../components/common/formikFields/FormikTextField";
import SubmitModalWithFormik from "../../../components/common/modalPopup/SubmitModalWithFormik";

interface FdpMappingModalProps {
  parcelCenterId: string;
  closeModal: CallableFunction;
  onCompleted: CallableFunction;
}

const AddFdpMappingModal: React.FC<FdpMappingModalProps> = ({
  parcelCenterId,
  closeModal,
  onCompleted,
}) => {
  // import fdp data from "Redux-Store"
  const { fdpList, fdpParcelCenterMappingList } = useSelector(
    (state: RootState) => state.fdp
  );

  const dispatch = useAppDispatch();

  const [ranks, setRanks] = useState<number[]>([]);

  const [fdpIds, setFdpIds] = useState<number[]>([]);

  useEffect(() => {
    getAllFdpPcMappings();
  }, []);

  useEffect(() => {
    const filteredMappings = fdpParcelCenterMappingList.filter(
      (e) => e.parcelCenterId === parseInt(parcelCenterId)
    );

    setFdpIds(Array.from(new Set(filteredMappings.map((e) => e.fdpId))));

    setRanks(Array.from(new Set(filteredMappings.map((e) => e.ranking))));
  }, [fdpParcelCenterMappingList, parcelCenterId]);

  // get all available FDP(s)
  const getAllFdpPcMappings = useCallback(async () => {
    try {
      const response = await dispatch(
        getfdpToParcelCenterMappingList()
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "fdpData",
        });
        return;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpDataException",
      });
    }
  }, [dispatch]);

  const validationSchema = Yup.object().shape({
    fdpMapping: Yup.array().of(
      Yup.object().shape({
        fdpId: Yup.number()
          .required("FDP is required")
          .test(
            "checkRankIsAlreadySelected",
            "This FDP is already mapped for this parcel center.",
            (value) => {
              return fdpIds.includes(value) === false;
            }
          ),
        ranking: Yup.number()
          .required("Rank is required")
          .test(
            "checkRankIsAlreadySelected",
            "This priority level is already reserved by one of the FDP for this parcel center.",
            (value) => {
              return ranks.includes(value) === false;
            }
          ),
      })
    ),
  });

  const initialValues: { fdpMapping: MultiFdpPcMapping[] } = {
    fdpMapping: [
      {
        fdpId: "",
        ranking: "",
      },
    ],
  };

  const handleSubmit = async (
    values: any,
    { setFieldError, resetForm }: any
  ) => {
    const fromResponse: MultiFdpPcMapping[] = values.fdpMapping;
    const newFdpIds = fromResponse.map((e) => e.fdpId);
    const newRankings = fromResponse.map((e) => e.ranking);

    const duplicateFdpIds = findDuplicatesAndIndices(newFdpIds);
    const duplicateRankings = findDuplicatesAndIndices(newRankings);
    if (
      Object.keys(duplicateFdpIds).length ||
      Object.keys(duplicateRankings).length
    ) {
      Object.keys(duplicateFdpIds).forEach((key) => {
        duplicateFdpIds[parseInt(key)].forEach((index) => {
          setFieldError(
            `fdpMapping.${index}.fdpId`,
            "Duplicate FDP has been selected."
          );
        });
      });

      Object.keys(duplicateRankings).forEach((key) => {
        duplicateRankings[parseInt(key)].forEach((index) => {
          setFieldError(
            `fdpMapping.${index}.ranking`,
            "Duplicate Priority level has been entered."
          );
        });
      });
      return;
    }
    const request: AddParcelCenterFdpMappingRequest = {
      parcelCenterId: parseInt(parcelCenterId),
      fdpMappings: fromResponse,
      createdBy: values.createdBy,
      updatedBy: values.updatedBy,
    };
    try {
      const response = await dispatch(addFdpMapping(request)).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, {
          toastId: "addParcelCenterSuccess",
        });
        onCompleted(request);
      } else {
        toast.error(response.message!, {
          toastId: "addParcelCenterError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "addParcelCenterException",
      });
    } finally {
      resetForm();
    }
    return true;
  };

  const findDuplicatesAndIndices = (
    arr: (number | "")[]
  ): { [key: number]: number[] } => {
    const duplicates: { [key: number]: number[] } = {};
    const seen: { [key: number]: number } = {};

    arr.forEach((item, index) => {
      if (item !== "") {
        if (seen[item] !== undefined) {
          if (duplicates[item] === undefined) {
            duplicates[item] = [seen[item]];
          }
          duplicates[item].push(index);
        } else {
          seen[item] = index;
        }
      }
    });

    return duplicates;
  };
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting, setFieldError, resetForm }) => {
        setSubmitting(true);
        handleSubmit(values, { setFieldError, resetForm }).finally(() =>
          setSubmitting(false)
        );
      }}
    >
      {({ values, isSubmitting, setFieldValue }) => {
        const selectedFdpIdsInForm = values.fdpMapping
          .filter((mapping) => mapping.fdpId != "")
          .map((mapping) => mapping.fdpId);
        const selectedRanksInForm = values.fdpMapping
          .filter((mapping) => mapping.ranking != "")
          .map((mapping) => mapping.ranking);

        const filteredFdpList = fdpList.filter(
          (e) =>
            e.active &&
            !selectedFdpIdsInForm.includes(e.id) &&
            !fdpIds.includes(e.id)
        );
        const filteredRanks = ranks.filter(
          (rank) => !selectedRanksInForm.includes(rank) && !ranks.includes(rank)
        );
        return (
          <SubmitModalWithFormik
            title="Add FDP to Parcel Center"
            closeSubmitModal={closeModal}
            allowSubmit={true}
            size="lg"
            isLoading={isSubmitting}
            submitButtonName="Add"
            closeModalAfterSubmit={false}
            key="submitModal"
          >
            {ranks.length !== 0 && (
              <Typography>
                Note: Rank number(s){" ["}
                {ranks.sort().join(", ")}
                {"] "}
                are already mapped to some FDP(s) of this parcel center.
              </Typography>
            )}
            <Form>
              <FieldArray name="fdpMapping">
                {({ push, remove }) => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {values.fdpMapping.map((_, index) => (
                      <Grid
                        container
                        rowSpacing={2}
                        columnSpacing={2}
                        justifyContent="center"
                        alignItems="center"
                        key={index}
                      >
                        <Grid item xs={12} sm={6} md={6} lg={6}>
                          <FormikTextField
                            margin="normal"
                            id={`fdpMapping.${index}.fdpId`}
                            label="FDP"
                            name={`fdpMapping.${index}.fdpId`}
                            size="small"
                            fullWidth
                            required
                            select
                            variant={"outlined"}
                            SelectProps={{
                              MenuProps: {
                                PaperProps: { style: { maxHeight: 150 } },
                              },
                            }}
                            onChange={(e: any) => {
                              const selectedFdpId = e.target.value;
                              setFieldValue(
                                `fdpMapping.${index}.fdpId`,
                                selectedFdpId
                              );
                            }}
                          >
                            {filteredFdpList.map((e, index) => (
                              <MenuItem
                                key={e.id + index}
                                value={e.id.toString()}
                              >
                                {e.companyName}
                              </MenuItem>
                            ))}
                          </FormikTextField>
                        </Grid>
                        <Grid item xs={10} sm={5} md={5} lg={5}>
                          <FormikTextField
                            margin="normal"
                            id={`fdpMapping.${index}.ranking`}
                            label="Priority"
                            name={`fdpMapping.${index}.ranking`}
                            size="small"
                            fullWidth
                            required
                            variant={"outlined"}
                            onChange={(e: any) => {
                              setFieldValue(
                                `fdpMapping.${index}.ranking`,
                                e.target.value
                              );
                            }}
                          />
                        </Grid>
                        <Grid item xs={2} sm={1} md={1} lg={1}>
                          <IconButton
                            onClick={() => push({ fdpId: "", ranking: "" })}
                          >
                            <AddCircleOutlineOutlined />
                          </IconButton>
                          {values.fdpMapping.length > 1 && (
                            <IconButton
                              onClick={() => {
                                remove(index);
                              }}
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                )}
              </FieldArray>
            </Form>
          </SubmitModalWithFormik>
        );
      }}
    </Formik>
  );
};

export default AddFdpMappingModal;
